import { IConcept, IShopConcept } from '@cbgms/api/modules/concepts'
import { isRequired } from '@cbgms/base/utils/validations'
import Checkbox from '@cbgms/components/form/Checkbox'
import Input from '@cbgms/components/form/Input'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { FieldArray, WrappedFieldArrayProps } from 'redux-form'
import { Button, Card, Divider, DropdownItemProps, Form } from 'semantic-ui-react'
import ConceptsInfoPopup from './ConceptsInfoPopup'
import { useDispatch } from 'react-redux'
import { postShopToCRM } from 'app-support/shops/state/actions'
import { EmptyListDateTime, formatDateTime } from '@cbgms/base/utils/datetime'
import { useNotifier } from '@cbgms/base/notifier'
import { FormLayoutContext } from '@cbgms/components/layout/FormLayout'

interface Props {
  field: string
  name: string
  onChange: (conceptUUID: string) => () => void
  shopUUID: string
  shopConcept: IShopConcept
}

const ConceptLine: React.FC<Props> = ({ name, field, onChange, shopConcept, shopUUID }) => {
  const conceptUUID = shopConcept.ConceptUUID
  const shopReferenceUUID = shopConcept.ShopReferenceUUID

  const { t } = useTranslation()
  const [isEditting, setEditting] = React.useState(false)
  const { handleSubmit, change } = React.useContext(FormLayoutContext)

  const onChangeSubmit = React.useMemo(() => handleSubmit(onChange(conceptUUID)), [conceptUUID, onChange])
  const toggleEditting = async () => {
    if (isEditting) {
      await change(`${field}.IsEnabled`, true)
      onChangeSubmit()
    }

    setEditting(!isEditting)
  }

  const setShopReferenceUUID = () => {
    change(`${field}.ShopReferenceUUID`, shopUUID)

    if (!isEditting) {
      setEditting(true)
    }
  }

  const hasIframeInformation = shopConcept.IFrameTermsAndConditionsAccepted !== EmptyListDateTime

  return (
    <Card className='h100' data-testid='shops-concepts-item'>
      <Card.Content>
        <Card.Header>
          {name}
          <div className='right floated'>
            <ConceptsInfoPopup disabled={!shopReferenceUUID} shopReferenceUUID={shopReferenceUUID} conceptUUID={conceptUUID} />
          </div>
        </Card.Header>
      </Card.Content>
      <Card.Content>
        <Checkbox
          data-testid='shop-concepts-line-enabled'
          name={`${field}.IsEnabled`}
          toggle
          label={t('Shop.EnableTenderEngine')}
          onChange={async (_event, newValue) => {
            // Hack to make sure the value updates before submitting
            await change(`${field}.IsEnabled`, newValue)
            onChangeSubmit()
          }}
        />
        <Input
          data-testid='shop-concepts-line-tenderuuid'
          maxLength='36'
          disabled={!isEditting}
          name={`${field}.ShopReferenceUUID`}
          validate={isEditting ? isRequired : undefined}
        />
        <Button.Group widths={2}>
          <Button
            data-testid='shop-concepts-line-createshop'
            onClick={setShopReferenceUUID}
            content={t('Shop.CreateTenderShop')}
            className='no-wrap'
          />
          <Button data-testid='shop-concepts-line-toggleedit' icon={isEditting ? 'save' : 'pencil'} onClick={toggleEditting} />
        </Button.Group>
      </Card.Content>
      {hasIframeInformation && (
        <Card.Content textAlign='center'>
          <p>{t('ConceptLine.IFrameTermsAndConditionsAccepted', { date: formatDateTime(shopConcept.IFrameTermsAndConditionsAccepted) })}</p>
        </Card.Content>
      )}
    </Card>
  )
}

interface IConceptLinesProps {
  concepts: IConcept[]
  shopUUID: string
  shopID: string
  companyUUID: string
  onSubmit: (conceptUUID: string) => () => void
  onConceptAdd: (conceptUUID: string) => () => void
}

interface ConceptLinesProps extends IConceptLinesProps, WrappedFieldArrayProps<IShopConcept> {}

function conceptsToOptions(concepts: IConcept[], shopsInConcept: IShopConcept[]): DropdownItemProps[] {
  return concepts.reduce<DropdownItemProps[]>((previousValue, currentValue) => {
    const isShopAlreadySubscribedToConcept = shopsInConcept.some(shopInConcept => shopInConcept.ConceptUUID === currentValue.UUID)

    if (!isShopAlreadySubscribedToConcept) {
      previousValue.push({
        key: currentValue.UUID,
        text: currentValue.Name,
        value: currentValue.UUID
      })
    }

    return previousValue
  }, [])
}

const ConceptLines: React.FC<ConceptLinesProps> = ({ concepts, onSubmit, onConceptAdd, fields, meta, shopUUID, companyUUID, shopID }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const notifier = useNotifier()
  const [selectedConcept, setSelectedConcept] = React.useState('')
  const { handleSubmit } = React.useContext(FormLayoutContext)
  const conceptOptions = conceptsToOptions(concepts, fields.getAll())

  const addConcept = async () => {
    if (selectedConcept === '') {
      return
    }

    setSelectedConcept('')

    await fields.push({
      ShopUUID: shopUUID,
      ConceptUUID: selectedConcept,
      ShopReferenceUUID: '',
      IsEnabled: false,
      CompanyUUID: companyUUID,
      IFrameTermsAndConditionsAccepted: EmptyListDateTime
    })

    await handleSubmit(onConceptAdd(selectedConcept))()
  }

  const addShopToCRM = async () => {
    await dispatch(postShopToCRM(shopID))
    notifier.success(t('Shop.AddToCRMSuccess'))
  }

  return (
    <React.Fragment>
      <Form.Group>
        <Form.Dropdown
          data-testid='shops-concepts-select'
          value={selectedConcept}
          label={t('Shop.SelectConcept')}
          selectOnBlur={false}
          onChange={(_, { value }) => setSelectedConcept(value as string)}
          selection
          fluid
          options={conceptOptions}
          width={5}
        />
        <Form.Button data-testid='shops-concepts-add' disabled={selectedConcept === ''} icon='add' label='&nbsp;' onClick={addConcept} />
        <Form.Button content={t('Shop.AddToCRM')} data-testid='shop-add-to-crm' label='&nbsp;' onClick={addShopToCRM} />
      </Form.Group>

      {fields.length > 0 ? <Divider /> : null}
      <Card.Group itemsPerRow={4} stackable>
        {fields.map((field, i) => {
          const shopConcept = fields.get(i)
          const relatedConcept = concepts.find(concept => concept.UUID === shopConcept.ConceptUUID)
          return (
            <ConceptLine
              key={i}
              field={field}
              onChange={onSubmit}
              shopUUID={shopUUID}
              shopConcept={shopConcept}
              name={relatedConcept ? relatedConcept.Name : ''}
            />
          )
        })}
      </Card.Group>
      {meta.error}
    </React.Fragment>
  )
}

type ConceptsProps = IConceptLinesProps

const Concepts: React.FC<ConceptsProps> = props => {
  return <FieldArray {...props} name='ShopConcepts' component={ConceptLines} />
}

Concepts.displayName = 'Concepts'

export default Concepts
